import { damagePlayer } from './player';
import { playCactusDeathSound } from './audio';

// Frame layout (37×37 per frame, 6 cols × 3 rows):
// Row 0 (0–5):  Hidden, Wake 01–05
// Row 1 (6–11): Stand, Walk 01–04, Damage
// Row 2 (12–15): Dead 01–04
const CACTI_FRAMES = {
  hidden:     0,
  wakeStart:  1,
  wakeEnd:    5,
  stand:      6,
  walkStart:  7,
  walkEnd:    10,
  damage:     11,
  dieStart:   12,
  dieEnd:     15,
};

const DETECT_RANGE = 80;

const CONFIGS = [
  { x: 250, y: 250 },   // original — near slingshot platform
  { x: 464, y: 160 },   // right of slingshot
  { x: 1040, y: 320 },  // 14 tiles down, 4 tiles left of 1up
];

export function createCactus(scene) {
  scene.cacti = [];

  scene.anims.create({
    key: 'cactiWake',
    frames: scene.anims.generateFrameNumbers('cacti', { start: CACTI_FRAMES.wakeStart, end: CACTI_FRAMES.wakeEnd }),
    frameRate: 6,
    repeat: 0
  });
  scene.anims.create({
    key: 'cactiSleep',
    frames: scene.anims.generateFrameNumbers('cacti', { frames: [5, 4, 3, 2, 1, 0] }),
    frameRate: 6,
    repeat: 0
  });
  scene.anims.create({
    key: 'cactiWalk',
    frames: scene.anims.generateFrameNumbers('cacti', { start: CACTI_FRAMES.walkStart, end: CACTI_FRAMES.walkEnd }),
    frameRate: 6,
    repeat: -1
  });
  scene.anims.create({
    key: 'cactiDie',
    frames: scene.anims.generateFrameNumbers('cacti', { start: CACTI_FRAMES.dieStart, end: CACTI_FRAMES.dieEnd }),
    frameRate: 6,
    repeat: 0
  });

  for (const cfg of CONFIGS) addCactus(scene, cfg);
}

function addCactus(scene, cfg) {
  const cactus = scene.physics.add.sprite(cfg.x, cfg.y, 'cacti', CACTI_FRAMES.hidden);
  cactus.setScale(1);
  cactus.setBounce(0.2);
  cactus.setCollideWorldBounds(true);
  cactus.health = 3;
  cactus.body.setSize(16, 28);
  cactus.body.setOffset(10, 8);
  cactus.knockbackTimer = 0;
  cactus.isDying = false;
  cactus.aggroTimer = 0;
  cactus.state = 'hidden';

  cactus.hit = (facing) => hitCactus(scene, cactus, facing);

  scene.physics.add.overlap(scene.player, cactus, () => {
    if (cactus.isDying) return;
    const alreadyInvincible = scene.playerInvincible;
    damagePlayer(scene, 0.5);
    if (scene.player.isDead) return;
    if (alreadyInvincible) return;
    if (scene.player.isClimbing || scene.player.isPerched) return;
    const knockDir = scene.player.x < cactus.x ? -1 : 1;
    scene.player.setVelocityX(knockDir * 180);
    scene.player.knockbackTimer = 300;
  }, null, scene);

  scene.physics.add.collider(cactus, scene.environmentLayer);
  scene.physics.add.collider(cactus, scene.ladderBlockers);

  scene.cacti.push(cactus);
}

function killCactus(scene, cactus) {
  if (cactus.isDying) return;
  cactus.isDying = true;
  playCactusDeathSound();
  cactus.setVelocity(0, 0);
  cactus.body.setAllowGravity(false);
  cactus.body.setImmovable(true);
  cactus.anims.play('cactiDie');
  cactus.once('animationcomplete-cactiDie', () => cactus?.destroy());
}

export function updateCactus(scene, delta) {
  for (const cactus of scene.cacti) {
    if (!cactus?.active) continue;
    if (cactus.isDying) continue;

    if (cactus.knockbackTimer > 0) {
      cactus.knockbackTimer -= delta;
      continue;
    }

    const distanceX = scene.player.x - cactus.x;

    const distanceY = scene.player.y - cactus.y;

    if (cactus.state === 'hidden') {
      if (Math.abs(distanceX) < DETECT_RANGE && Math.abs(distanceY) < 80) {
        cactus.state = 'waking';
        cactus.anims.play('cactiWake');
        cactus.once('animationcomplete-cactiWake', () => {
          if (cactus?.active && !cactus.isDying) {
            cactus.state = 'chase';
            cactus.anims.play('cactiWalk');
          }
        });
      }
      continue;
    }

    if (cactus.state === 'waking') {
      cactus.setVelocityX(0);
      continue;
    }

    if (cactus.state === 'sleeping') {
      cactus.setVelocityX(0);
      continue;
    }

    if (cactus.aggroTimer > 0) cactus.aggroTimer -= delta;

    // Chase — return to hidden if player gets far away (unless recently hit)
    if (cactus.aggroTimer <= 0 && Math.abs(distanceX) > DETECT_RANGE * 1.5) {
      cactus.state = 'sleeping';
      cactus.setVelocityX(0);
      cactus.anims.play('cactiSleep');
      // 6 frames at 6fps = 1000ms
      scene.time.delayedCall(1000, () => {
        if (cactus?.active && !cactus.isDying && cactus.state === 'sleeping') {
          cactus.state = 'hidden';
          cactus.setFrame(CACTI_FRAMES.hidden);
        }
      });
      continue;
    }

    const speed = 65;
    if (Math.abs(distanceX) > 5) {
      let vx = Math.sign(distanceX) * speed;
      if (Math.abs(distanceX) < 50) {
        vx *= 0.6;
        vx += (Math.random() - 0.5) * 10;
      }
      cactus.setVelocityX(vx);
    } else {
      cactus.setVelocityX(0);
    }
    cactus.flipX = cactus.body.velocity.x < 0;
  }

  scene.cacti = scene.cacti.filter(c => c.active);
}

function hitCactus(scene, cactus, facing) {
  if (!cactus?.active || cactus.isDying) return;
  cactus.health -= 1;
  cactus.anims.stop();
  cactus.aggroTimer = 4000;
  cactus.state = 'chase';
  cactus.setVelocityX(facing * 220);
  cactus.knockbackTimer = 500;
  cactus.setFrame(CACTI_FRAMES.damage);
  scene.time.delayedCall(150, () => {
    if (cactus?.active && !cactus.isDying) {
      cactus.anims.play('cactiWalk', true);
    }
  });
  if (cactus.health <= 0) killCactus(scene, cactus);
}
